import React, { useContext } from "react";
import { v4 as uuid } from "uuid";

import { ElementState, Preview } from "@creatomate/preview";
import { Button, Icon, Tooltip } from "@culturehq/components";

import { useProfile } from "app/context";
import { deepClone, t, Trans } from "app/utils";

import { timeToNumber } from "components/stories/video-studio/utils";
import { CreatomateContext } from "../../contexts/VideoStudioContext";
import { splitSubtitle } from "../../sidebar/tabs/subtitle/utils";
import { VIDEO_TYPE } from "../../utils/constants";
import { usePreviewTime } from "../../contexts/TimeContext";

interface SplitButtonProps {
  disabled?: boolean;
  handleSplit: () => Promise<void>;
}

const SplitButton: React.FC<SplitButtonProps> = ({ children, disabled, handleSplit }) => (
  <Button
    className="Split-Btn"
    onClick={handleSplit}
    disabled={disabled}
  >
    {children}
  </Button>
);

interface SplitProps {
  preview?: Preview;
}

const Split: React.FC<SplitProps> = ({ preview }) => {
  const { time } = usePreviewTime();
  const { data, getActiveElement, handleUpdateData } = useContext(CreatomateContext);
  const { subtitles } = data;

  const activeElement = preview ? getActiveElement(preview) : undefined;
  const activeElementTime = activeElement?.globalTime || 0;
  const activeElementTrimStart = timeToNumber(activeElement?.source?.trim_start);

  const { profile: { organization } } = useProfile();

  const isVideoType = (e: ElementState | undefined) => e?.source?.type === VIDEO_TYPE;
  const isAudioType = (e: ElementState | undefined) => e?.source?.type === "audio";


  const getElementToSplit = (): ElementState | undefined => {
    if (!activeElement) return undefined;

    if ((isVideoType(activeElement) || isAudioType(activeElement))
        && activeElement.globalTime <= time
        && (activeElement.globalTime + activeElement.duration) >= time) {
      return activeElement;
    }

    return preview?.state?.elements?.filter(
      element => element.source.type === activeElement.source.type
    ).find(element => (
      (isVideoType(element) || isAudioType(element))
        && element.globalTime <= time
        && (element.globalTime + element.duration) >= time
    ));
  };

  const handleSplit = async () => {
    if (!activeElement) return;

    const elementToSplit = getElementToSplit();
    if (!elementToSplit) return;

    const rawSpeed = activeElement.source.speed ?? "100%";
    const speed = (() => {
      const numeric = parseFloat(rawSpeed.replace("%", ""));
      // eslint-disable-next-line no-restricted-globals
      return isNaN(numeric) ? 1 : numeric / 100;
    })();

    const elementToSplitTime = elementToSplit.globalTime || 0;
    const elementToSplitTrimStart = timeToNumber(elementToSplit.source.trim_start);
    const source = preview?.getSource();
    if (!source) return;

    await preview?.pause();

    const { source: properties } = elementToSplit;
    const splittedTime = time - elementToSplit.globalTime;
    const elements: Record<string, any>[] = deepClone(source.elements || []);
    const toUpdateElementIdx = elements.findIndex(e => e.id === properties.id);
    const updateLeftElement = (() => {
      elements[toUpdateElementIdx].duration = splittedTime;
    })();

    const createRightElement = (() => {
      const newElement: Record<string, any> = {
        ...properties,
        id: uuid(),
        time,
        trim_start: elementToSplitTrimStart + (splittedTime * speed),
        duration: elementToSplit.duration - splittedTime
      };
      elements.push(newElement);
    })();

    if (isVideoType(elementToSplit)) {
      const updatedElements = await splitSubtitle(
        subtitles,
        time,
        handleUpdateData,
        organization.name || "",
        elements,
        preview?.state?.elements
      );
      await preview?.setSource({ ...source, elements: updatedElements }, true);
    } else {
      await preview?.setSource({ ...source, elements }, true);
    }

    await preview?.setTime(time + 0.001);
  };

  const getSplitLabel = () => {
    const elementToSplit = getElementToSplit();
    if (isVideoType(elementToSplit)) return <Trans>Split Video</Trans>;
    if (isAudioType(elementToSplit)) return <Trans>Split Audio</Trans>;
    return <Trans>Split</Trans>;
  };

  if (!isVideoType(activeElement) && !isAudioType(activeElement)) {
    return (
      <Tooltip tip={t("Click on the video/audio you would like to split")}>
        <SplitButton disabled handleSplit={handleSplit}>
          <Icon icon="scissors" />
          {getSplitLabel()}
        </SplitButton>
      </Tooltip>
    );
  }

  return (
    <SplitButton handleSplit={handleSplit}>
      <Icon icon="scissors" />
      {getSplitLabel()}
    </SplitButton>
  );
};

export default Split;
